import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { AxiosError } from 'axios';

import { handleTTS, audioManager, type QtiQuestionDTO, type SerializedHighlight } from '@/src/lib';
import {
    ContextMenu,
    useContextMenu,
    type MenuAction,
    type ContextMenuInputItem,
    VolumeIcon,
    Container,
    SimpleButton,
    Loading,
    ErrorMessage,
} from '@/src/ui';
import AssessmentNavigationMatrix from './components/AssessmentNavigationMatrix';
import { QuestionRenderer } from '@/src/lib';
import QuestionTools from './components/QuestionTools';
import FinishSegmentButton from './components/FinishSegmentButton';
import {
    ExamNotepad,
    HighlightableWrapper,
    getHighlightIdFromElement,
    type ActiveTool,
    type HighlightableWrapperHandle,
    type HighlightContextMenuData,
} from './components/studentTools';
import { apiRequest } from '@/src/services';
import type {
    ExamDetailsDTO,
    StudentExamViewLoadDTO,
    AttemptQuestionDetailsDTO,
} from './models/StudentExamViewDTOs';
import { Constants, translate } from '@/src/utils';
const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

import './ViewQuestionPage.css';
import ExitAssessmentModal from './components/ExitAssessmentModal';

type HighlightMap = { en: SerializedHighlight[]; es: SerializedHighlight[] };

type UnifiedMenuData = {
    selectedText: string;
    highlightId: string | null;
    hasSelection: boolean;
    choiceData?: ContextMenuInputItem;
    choiceActions?: MenuAction<ContextMenuInputItem>[];
};

const ViewQuestionPage: React.FC = () => {
    document.title = 'Exam View';

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [qiLoading, setQiLoading] = useState(false);
    const [error, setError] = useState('');
    const [notepadError, setNotepadError] = useState('');

    // Loaded exam and question state
    // Exam data - navigation, title, etc
    // Active question - current question being viewed
    // Savable question details - current SEAQ values - flagged, response, etc.
    const [examData, setExamData] = useState<ExamDetailsDTO | null>(null);
    const [activeQuestion, setActiveQuestion] = useState<QtiQuestionDTO | null>(null);
    const [savableQuestionDetails, setSavableQuestionDetails] =
        useState<AttemptQuestionDetailsDTO | null>(null);
    const [stimChildQuestionIds, setStimChildQuestionIds] = useState<number[] | null>(null);

    // User response state
    const [language, setLanguage] = useState<'en' | 'es'>('en');
    const userInteractedRef = useRef<boolean>(false);
    const lastSavedUserResponse = useRef<string>('{}');
    const [userResponse, setUserResponse] = useState<string>('{}');
    const [disableQuestionEdit, setDisableQuestionEdit] = useState<boolean>(false);

    // Highlight state (per-question, keyed by language)
    const [activeTool, setActiveTool] = useState<ActiveTool>('highlight');
    const highlightRef = useRef<HighlightableWrapperHandle>(null);
    const [highlights, setHighlights] = useState<SerializedHighlight[]>([]);
    const [highlightMap, setHighlightMap] = useState<HighlightMap>({ en: [], es: [] });
    const highlightsChangedRef = useRef(false);

    // Notepad state (per-attempt)
    const [notepadOpen, setNotepadOpen] = useState(false);
    const [notepadText, setNotepadText] = useState('');
    const notepadChangedRef = useRef(false);

    const { attemptId = '0', attemptQuestionId = '0' } = useParams();

    const navigate = useNavigate();

    const saveQuestion = useCallback(
        async (callback?: () => void, saveOnNavigate?: boolean) => {
            const onNavigateDidResponseChange =
                saveOnNavigate && userResponse !== lastSavedUserResponse.current;
            if (!userInteractedRef.current && !onNavigateDidResponseChange) {
                if (callback) callback();
                return;
            }

            try {
                setError('');
                setSaving(true);

                if (saveOnNavigate) {
                    setDisableQuestionEdit(true);
                }

                const submittedAnswer = userResponse.trim();
                const question = {
                    id: parseInt(attemptQuestionId),
                    submittedAnswer,
                };

                if (activeQuestion?.meta.questionTypeMasterId === qtm.STIMULUS) {
                    const oldUserResponse = lastSavedUserResponse.current
                        ? JSON.parse(lastSavedUserResponse.current)
                        : {};
                    const newUserResponse = userResponse ? JSON.parse(userResponse) : {};

                    for (const id of Object.keys(newUserResponse)) {
                        if (oldUserResponse[id] === newUserResponse[id]) {
                            continue;
                        } else {
                            const subQuestionToSave = {
                                id,
                                submittedAnswer: newUserResponse[id],
                            };
                            await apiRequest(
                                'patch',
                                `assessments/studentExamView/saveQuestion/${id}`,
                                subQuestionToSave
                            );
                        }
                    }
                } else {
                    await apiRequest(
                        'patch',
                        `assessments/studentExamView/saveQuestion/${attemptQuestionId}`,
                        question
                    );
                }

                lastSavedUserResponse.current = userResponse;
                setSavableQuestionDetails(
                    (prev) =>
                        ({
                            ...prev,
                            submittedAnswer,
                        }) as AttemptQuestionDetailsDTO
                );
                if (callback) callback();
            } catch (e) {
                setError(
                    'Failed to save question: ' + (e as AxiosError)?.response?.data ||
                        'Unknown error.'
                );
            } finally {
                setSaving(false);
                userInteractedRef.current = false;
                if (saveOnNavigate) {
                    setDisableQuestionEdit(false);
                }
            }
        },
        [attemptQuestionId, userResponse, activeQuestion?.meta.questionTypeMasterId]
    );
    const saveQuestionAutosave = useCallback(async () => {
        await saveQuestion();
    }, [saveQuestion]);
    const saveQuestionNavigate = useCallback(
        async (callback?: () => void) => {
            const savOnNavigate = true;
            await saveQuestion(callback, savOnNavigate);
        },
        [saveQuestion]
    );

    const saveHighlights = useCallback(async () => {
        if (!highlightsChangedRef.current || !savableQuestionDetails) {
            return;
        }
        try {
            const currentMeta = savableQuestionDetails.userInteractionMeta ?? {};
            const updatedMap = {
                ...highlightMap,
                [language]: highlights,
            };
            const updatedMeta = {
                ...currentMeta,
                highlights: updatedMap,
            };

            await apiRequest(
                'patch',
                `assessments/studentExamView/updateUserInteractionMeta/${attemptQuestionId}`,
                updatedMeta
            );

            setHighlightMap(updatedMap);
            setSavableQuestionDetails((prev) =>
                prev
                    ? {
                          ...prev,
                          userInteractionMeta: updatedMeta,
                      }
                    : prev
            );

            highlightsChangedRef.current = false;
        } catch {
            setError('Failed to save highlights');
        }
    }, [attemptQuestionId, highlights, highlightMap, language, savableQuestionDetails]);

    useDebouncedSaveEffect(highlightsChangedRef.current, saveHighlights, 1000);

    const handleHighlightsChange = useCallback(
        (next: SerializedHighlight[]) => {
            setHighlights(next);
            setHighlightMap((prev) => ({ ...prev, [language]: next }));
            highlightsChangedRef.current = true;
        },
        [language]
    );

    // Swap active highlights when language changes
    useEffect(() => {
        setHighlights(highlightMap[language] ?? []);
    }, [language, highlightMap]);

    const saveNotepad = useCallback(async () => {
        if (!notepadChangedRef.current) {
            return;
        }
        try {
            setNotepadError('');
            await apiRequest('patch', `assessments/studentExamView/saveNotepad/${attemptId}`, {
                notepadText,
            });
            notepadChangedRef.current = false;
        } catch {
            setNotepadError('Failed to save notepad');
        }
    }, [attemptId, notepadText]);

    useDebouncedSaveEffect(notepadChangedRef.current, saveNotepad, 2000);

    const handleNotepadChange = useCallback((content: string) => {
        setNotepadText(content);
        notepadChangedRef.current = true;
    }, []);

    const loadQuestion = useCallback(
        (suppressQuestionReload: boolean = false) => {
            (async function () {
                try {
                    audioManager.stop();
                    setError('');
                    setLoading(true);

                    setDisableQuestionEdit(true);
                    let parsedQuestion = null;
                    if (!suppressQuestionReload) {
                        const questionRes = await apiRequest<QtiQuestionDTO>(
                            'get',
                            `assessments/studentExamView/question?attemptId=${attemptId}&attemptQuestionId=${attemptQuestionId}`
                        );
                        parsedQuestion = questionRes.data || {};
                    }

                    const attemptQuestionRes = await apiRequest<StudentExamViewLoadDTO>(
                        'get',
                        `assessments/studentExamView/studentExamAttemptQuestion?attemptId=${attemptId}&attemptQuestionId=${attemptQuestionId}`
                    );
                    const attemptQuestionData = attemptQuestionRes.data || {};

                    const newestQuestionData = parsedQuestion || activeQuestion;
                    const newExamData = attemptQuestionData.examAttemptDetails;
                    if (newExamData && newestQuestionData) {
                        const currentQuestion = newExamData.navigationQuestions.find(
                            (q) => q.id === newestQuestionData.id
                        );
                        const newActiveQuestion = {
                            displayValue: currentQuestion?.displayValue || '',
                            ...newestQuestionData,
                        };
                        if (currentQuestion) {
                            setActiveQuestion(newActiveQuestion);
                        }
                    }

                    setExamData(attemptQuestionData.examAttemptDetails);
                    setNotepadText(attemptQuestionData.examAttemptDetails?.notepadText || '');
                    setSavableQuestionDetails(attemptQuestionData.attemptQuestionDetails);
                    setStimChildQuestionIds(
                        attemptQuestionData.stimulusChildAttemptQuestionIds || null
                    );

                    const newUserResponse =
                        attemptQuestionData.attemptQuestionDetails.submittedAnswer || '';
                    setUserResponse(newUserResponse);
                    lastSavedUserResponse.current = newUserResponse;

                    const meta = attemptQuestionData.attemptQuestionDetails.userInteractionMeta;
                    const hlMap = meta?.highlights ?? { en: [], es: [] };
                    setHighlightMap(hlMap);
                    setHighlights(hlMap[language] ?? []);
                    highlightsChangedRef.current = false;

                    if (!suppressQuestionReload && attemptQuestionData.examAttemptDetails) {
                        const np = attemptQuestionData.examAttemptDetails.notepadText ?? '';
                        setNotepadText(np);
                        notepadChangedRef.current = false;
                    }
                } catch (e) {
                    setActiveQuestion(null);
                    setError(
                        'Failed to load question details: ' + (e as AxiosError)?.response?.data ||
                            'Unknown error.'
                    );
                } finally {
                    setLoading(false);
                    setDisableQuestionEdit(false);
                }
            })();
        },
        [attemptId, attemptQuestionId, activeQuestion, language]
    );
    const toggleQuestionFlag = useCallback(
        (isFlaggedForReview: boolean) => {
            (async function () {
                try {
                    setLoading(true);
                    setError('');

                    const question = {
                        id: parseInt(attemptQuestionId),
                        isFlaggedForReview,
                    };

                    await apiRequest(
                        'patch',
                        `assessments/studentExamView/updateQuestionFlagStatus/${attemptQuestionId}`,
                        question
                    );
                } catch {
                    setError('Failed to toggle question flag.');
                } finally {
                    setLoading(false);
                    loadQuestion(true);
                }
            })();
        },
        [attemptQuestionId, loadQuestion]
    );

    useEffect(() => {
        loadQuestion();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attemptId, attemptQuestionId]);

    const handleUpdateUserResponse = (response: string, didUserInteract: boolean) => {
        setUserResponse(response);
        userInteractedRef.current = didUserInteract;
    };

    useDebouncedSave(userInteractedRef.current, savableQuestionDetails, saveQuestionAutosave);

    const {
        menu: unifiedMenu,
        open: openUnifiedMenu,
        close: closeUnifiedMenu,
        setContainerRef: setContainerRefForUnified,
    } = useContextMenu<UnifiedMenuData>();

    const unifiedActions: MenuAction<UnifiedMenuData>[] = useMemo(() => {
        const actions: MenuAction<UnifiedMenuData>[] = [];
        const data = unifiedMenu.data;

        if (data?.hasSelection) {
            actions.push({
                label: (
                    <div className="flex items-center justify-between w-full">
                        {translate(language, 'highlight_selection')}
                    </div>
                ),
                onClick: () => highlightRef.current?.addHighlight(),
            });
        }

        if (data?.highlightId) {
            actions.push({
                label: translate(language, 'remove_highlight'),
                onClick: () => {
                    if (data.highlightId) {
                        handleHighlightsChange(highlights.filter((h) => h.id !== data.highlightId));
                    }
                },
            });
        }

        if (data?.choiceActions) {
            for (const action of data.choiceActions) {
                actions.push({
                    label: action.label,
                    onClick: () => action.onClick(data.choiceData ?? null),
                });
            }
        }

        actions.push({
            label: (
                <div className="flex items-center justify-between w-full">
                    <span>Play audio</span>
                    <span>
                        <VolumeIcon />
                    </span>
                </div>
            ),
            onClick: (d) => handleTTS(d?.selectedText || '', setError),
        });

        return actions;
    }, [unifiedMenu.data, language, highlights, handleHighlightsChange]);

    const handleHighlightContextMenu = useCallback(
        (e: React.MouseEvent, data: HighlightContextMenuData) => {
            openUnifiedMenu(e, {
                selectedText: data.selectedText,
                highlightId: data.highlightId,
                hasSelection: data.hasSelection,
            });
        },
        [openUnifiedMenu]
    );

    const handleRendererContextMenu = useCallback(
        (
            e: React.MouseEvent,
            itemData: ContextMenuInputItem,
            rendererActions: MenuAction<ContextMenuInputItem>[]
        ) => {
            const selectionText = window.getSelection()?.toString().trim() || '';
            const hasSelection = selectionText.length > 0;
            const highlightId = getHighlightIdFromElement(e.target as HTMLElement);

            const ttsText =
                selectionText ||
                (highlightId && (e.target as HTMLElement).closest('mark')?.textContent?.trim()) ||
                (itemData?.language === 'en' ? itemData?.text : itemData?.text_es) ||
                '';

            openUnifiedMenu(e, {
                selectedText: ttsText,
                highlightId,
                hasSelection,
                choiceData: itemData,
                choiceActions: rendererActions,
            });
        },
        [openUnifiedMenu]
    );

    const [showExit, setShowExit] = useState(false);

    return (
        <div className="exam-page-wrapper">
            <Container className="user-min-font-size exam-container-wrapper">
                {showExit && <ExitAssessmentModal setShowExit={setShowExit} navigate={navigate} />}
                <div className="exam-container grid gap-2 overflow-auto">
                    <main className="order-2 lg:order-1 min-w-0 p-4 sm:p-6 lg:p-8">
                        {activeQuestion && savableQuestionDetails && (
                            <>
                                <div className="question-tools">
                                    <div className="assessment-question-label w-fit mt-2 text-center">
                                        {activeQuestion.meta.questionTypeMasterId === qtm.SEGMENT
                                            ? translate(language, 'segment_end')
                                            : `${savableQuestionDetails.displayValue}`}
                                    </div>

                                    <QuestionTools
                                        loading={loading || qiLoading || saving}
                                        setError={setError}
                                        language={language}
                                        setLanguage={setLanguage}
                                        showToggle={
                                            examData?.isSpanishAllowed &&
                                            activeQuestion.meta.isSpanishAllowed
                                                ? true
                                                : false
                                        }
                                        questionDetails={savableQuestionDetails}
                                        saveQuestion={saveQuestionNavigate}
                                        toggleQuestionFlag={toggleQuestionFlag}
                                        navData={examData?.navigationQuestions}
                                        isSegment={
                                            activeQuestion.meta.questionTypeMasterId === qtm.SEGMENT
                                        }
                                        activeTool={activeTool}
                                        onSetActiveTool={setActiveTool}
                                        onOpenNotepad={() => setNotepadOpen(true)}
                                    />
                                </div>

                                <HighlightableWrapper
                                    ref={highlightRef}
                                    highlights={highlights}
                                    onHighlightsChange={handleHighlightsChange}
                                    onContextMenu={handleHighlightContextMenu}
                                    activeTool={activeTool}
                                >
                                    <div key={`question-${savableQuestionDetails.id}`}>
                                        <QuestionRenderer
                                            question={activeQuestion}
                                            handleUpdateUserResponse={handleUpdateUserResponse}
                                            stimChildQuestionIds={stimChildQuestionIds || undefined}
                                            stimMode="sev"
                                            setQuestionLoading={setQiLoading}
                                            loadedUserInteractionMeta={
                                                savableQuestionDetails.userInteractionMeta
                                            }
                                            loadedUserResponse={
                                                savableQuestionDetails.submittedAnswer
                                            }
                                            options={{
                                                language,
                                                disabled: disableQuestionEdit,
                                                onRendererContextMenu: handleRendererContextMenu,
                                                showAudioButtons: true,
                                            }}
                                        />
                                    </div>
                                </HighlightableWrapper>

                                {activeQuestion.meta.questionTypeMasterId === qtm.SEGMENT && (
                                    <FinishSegmentButton
                                        loading={loading || qiLoading}
                                        setError={setError}
                                        language={language}
                                        questionDetails={savableQuestionDetails}
                                        navData={examData?.navigationQuestions}
                                    />
                                )}
                            </>
                        )}
                        <ErrorMessage error={error} />
                    </main>

                    <aside
                        id="exam-nav"
                        className="flex flex-col order-1 lg:order-2 lg:sticky lg:top-4 h-full max-h-[calc(100vh-2rem)] lg:self-start flex-none p-4 rounded-md w-full"
                    >
                        <div className="flex-1 overflow-y-auto pr-2">
                            {examData && (
                                <AssessmentNavigationMatrix
                                    questions={examData.navigationQuestions}
                                    loading={loading || qiLoading || saving}
                                    language={language}
                                    title={examData.examTitle}
                                    activeSegmentNumber={examData.activeSegmentNumber}
                                    selectedQuestionId={parseInt(attemptQuestionId ?? '0')}
                                    onButtonClick={async (
                                        questionRoute: string,
                                        replace: boolean
                                    ) => {
                                        await saveQuestionNavigate(() => {
                                            navigate(
                                                `/studentExamView/attempt/${attemptId}/${questionRoute}`,
                                                replace ? { replace: true } : undefined
                                            );
                                        });
                                    }}
                                />
                            )}

                            {(loading || qiLoading) && <Loading size="lg" />}
                            {saving && <Loading message="Saving..." size="lg" />}
                        </div>
                        <div className="flex-none pt-4 mt-4 flex justify-end">
                            <SimpleButton
                                onClick={() => setShowExit(true)}
                                className="assessment-nav-btn red"
                            >
                                Exit Exam
                            </SimpleButton>
                        </div>
                    </aside>
                </div>
            </Container>

            <ExamNotepad
                open={notepadOpen}
                onClose={() => setNotepadOpen(false)}
                content={notepadText}
                onChange={handleNotepadChange}
                language={language}
                disabled={loading}
                error={notepadError}
            />

            <ContextMenu
                visible={unifiedMenu.visible}
                x={unifiedMenu.x}
                y={unifiedMenu.y}
                actions={unifiedActions}
                data={unifiedMenu.data}
                onClose={closeUnifiedMenu}
                setContainerRef={setContainerRefForUnified}
            />
        </div>
    );
};

export default ViewQuestionPage;

function useDebouncedSave(
    userResponseChanged: boolean,
    savableQuestionDetails: AttemptQuestionDetailsDTO | null,
    saveQuestion: () => void
) {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (!userResponseChanged) {
            return;
        }

        timeoutRef.current = setTimeout(() => {
            saveQuestion();
        }, 1500);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [userResponseChanged, saveQuestion, savableQuestionDetails]);
}

function useDebouncedSaveEffect(changed: boolean, saveFn: () => void, delayMs: number) {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (!changed) {
            return;
        }

        timeoutRef.current = setTimeout(() => {
            saveFn();
        }, delayMs);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [changed, saveFn, delayMs]);
}
